import PdfModal from '@/components/reports/PdfModal';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, Loader2, X, Sparkles, Eye, Ban, Mail, Clock, Play, Settings2, MoreVertical, Code } from 'lucide-react';
import toast from 'react-hot-toast';
import { AgGridReact } from 'ag-grid-react';
import {
  AllEnterpriseModule,
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-enterprise';

const enterpriseModules = [AllEnterpriseModule];

const gridTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 12,
  rowHeight: 38,
  headerHeight: 38,
  borderRadius: 8,
});
import {
  fetchPdfBlobUrl,
  PDF_REPORTS,
  sendReportByEmail,
  executeReportSQLPreview,
  fetchReportSchedules,
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  fetchReportSettings,
  updateReportSettings,
  DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS,
  type PlantMonthlyCustomReportSettings,
  type PdfReportDef,
  type ReportOpenOptions,
  type ReportSchedule,
} from '@/api/reports';
import { fetchUsers, type UserListItem } from '@/api/users';
import { fetchInvestors, type Investor } from '@/api/investors';
import { fetchPowerPlants, type PowerPlant } from '@/api/powerplants';
import BlockingStatusOverlay from '@/components/ui/BlockingStatusOverlay';
import DropdownMenu, { type DropdownItem } from '@/ui/common/DropdownMenu';
import { confirmToast, useErrorToast } from '@/lib/notify';

type ReportRangePreset = 'thisMonth' | 'thisYear' | 'lastMonth' | 'all' | 'custom';

function toInputMonth(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function getMonthBounds(value: string) {
  const [yearRaw, monthRaw] = value.split('-');
  const year = Number(yearRaw);
  const monthIndex = Number(monthRaw) - 1;
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  return {
    startDate: toInputDate(start),
    endDate: toInputDate(end),
  };
}

function toInputDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDefaultRange() {
  const now = new Date();
  return {
    startDate: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: toInputDate(now),
    preset: 'thisMonth' as ReportRangePreset,
    label: 'Bu Ay',
  };
}

function getRangeFromPreset(preset: ReportRangePreset) {
  const now = new Date();
  const today = toInputDate(now);

  if (preset === 'thisYear') {
    return {
      startDate: toInputDate(new Date(now.getFullYear(), 0, 1)),
      endDate: today,
      label: 'Bu Yıl',
    };
  }

  if (preset === 'lastMonth') {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return {
      startDate: toInputDate(start),
      endDate: toInputDate(end),
      label: 'Geçen Ay',
    };
  }

  if (preset === 'all') {
    return {
      startDate: '',
      endDate: '',
      label: 'Tümü',
    };
  }

  return {
    startDate: toInputDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: today,
    label: 'Bu Ay',
  };
}

// ─── PDF Önizleme Modal ───────────────────────────────────────────────────────
function PowerPlantReportFilterModal({
  report,
  investors,
  users,
  loading,
  onClose,
  onSend,
  onConfirm,
  powerPlants,
  initialInvestorUuid,
  initialPowerPlantUuids,
}: {
  report: PdfReportDef;
  investors: Investor[];
  users: UserListItem[];
  loading: boolean;
  onClose: () => void;
  onSend: (payload: {
    investor: Investor | null;
    email: string;
    startDate: string | null;
    endDate: string | null;
    rangeLabel: string;
    powerPlantUuids?: string[];
  }) => void;
  onConfirm: (payload: {
    investor: Investor | null;
    startDate: string | null;
    endDate: string | null;
    rangeLabel: string;
    powerPlantUuids?: string[];
  }) => void;
  powerPlants?: PowerPlant[];
  initialInvestorUuid?: string | null;
  initialPowerPlantUuids?: string[] | null;
}) {
  const [selectedInvestorUuid, setSelectedInvestorUuid] = useState(initialInvestorUuid ?? '');
  const [selectedEmail, setSelectedEmail] = useState('');
  const [mailSending, setMailSending] = useState(false);
  const defaults = getDefaultRange();
  const [preset, setPreset] = useState<ReportRangePreset>(defaults.preset);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedMonth, setSelectedMonth] = useState(() => toInputMonth(new Date()));
  const [selectedPowerPlantUuids, setSelectedPowerPlantUuids] = useState<string[]>(initialPowerPlantUuids ?? []);
  const [selectedMonthStart, setSelectedMonthStart] = useState(() => {
    const now = new Date();
    const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    return toInputMonth(lastYear);
  });
  const [selectedMonthEnd, setSelectedMonthEnd] = useState(() => toInputMonth(new Date()));

  const presetOptions: { id: ReportRangePreset; label: string }[] = [
    { id: 'thisMonth', label: 'Bu Ay' },
    { id: 'thisYear', label: 'Bu Yıl' },
    { id: 'lastMonth', label: 'Geçen Ay' },
    { id: 'all', label: 'Tümü' },
    { id: 'custom', label: 'Özel' },
  ];
  const hasDateRange = report.id === 'powerplants';
  const hasMonthSelection = report.id === 'investor-monthly' || report.id === 'plant-monthly' || report.id === 'plant-monthly-custom' || report.id === 'plant-budget-vs-actual';
  const hasPowerPlantSelection = report.id === 'plant-daily' || report.id === 'plant-monthly' || report.id === 'plant-monthly-custom' || report.id === 'inverter-daily' || report.id === 'plant-budget-vs-actual';
  const hasSingleDateSelection = report.id === 'plant-daily' || report.id === 'inverter-daily';
  const canSendMail =
    report.id === 'powerplants'
    || report.id === 'problematic-strings-pdf'
    || report.id === 'investor-daily'
    || report.id === 'investor-monthly'
    || report.id === 'plant-daily'
    || report.id === 'plant-monthly'
    || report.id === 'plant-monthly-custom'
    || report.id === 'inverter-daily'
    || report.id === 'plant-budget-vs-actual';
  const requiresInvestor = report.id === 'investor-daily' || report.id === 'investor-monthly';
  const requiresSinglePlant = report.id === 'plant-monthly-custom';
  const hasMonthRangeSelection = report.id === 'plant-budget-vs-actual';

  const buildPayload = () => {
    const selectedInvestor = investors.find((item) => item.investor_uuid_id === selectedInvestorUuid) ?? null;
    const rangeLabel =
      preset === 'custom'
        ? `${startDate || 'Başlangıç yok'} - ${endDate || 'Bugün'}`
        : getRangeFromPreset(preset).label;

    let computedStartDate: string | null = null;
    let computedEndDate: string | null = null;
    let computedRangeLabel = 'Tümü';

    if (hasMonthRangeSelection) {
      computedStartDate = getMonthBounds(selectedMonthStart).startDate;
      computedEndDate = getMonthBounds(selectedMonthEnd).endDate;
      computedRangeLabel = `${selectedMonthStart} - ${selectedMonthEnd}`;
    } else if (hasDateRange) {
      computedStartDate = preset === 'all' ? null : (startDate || null);
      computedEndDate = preset === 'all' ? null : (endDate || null);
      computedRangeLabel = rangeLabel;
    } else if (hasMonthSelection) {
      computedStartDate = getMonthBounds(selectedMonth).startDate;
      computedEndDate = getMonthBounds(selectedMonth).endDate;
      computedRangeLabel = selectedMonth;
    } else if (hasSingleDateSelection) {
      computedStartDate = startDate || null;
      computedEndDate = startDate || null;
      computedRangeLabel = startDate || 'Bugün';
    }

    return {
      investor: selectedInvestor,
      startDate: computedStartDate,
      endDate: computedEndDate,
      rangeLabel: computedRangeLabel,
      powerPlantUuids: hasPowerPlantSelection ? selectedPowerPlantUuids : undefined,
    };
  };

  const visiblePowerPlants = useMemo(() => {
    const allPlants = powerPlants ?? [];
    if (!selectedInvestorUuid) return allPlants;
    const investorName = investors.find((item) => item.investor_uuid_id === selectedInvestorUuid)?.investor_name;
    if (!investorName) return allPlants;
    return allPlants.filter((plant) => plant.power_plant_investor_name === investorName);
  }, [investors, powerPlants, selectedInvestorUuid]);

  useEffect(() => {
    if (initialInvestorUuid) {
      setSelectedInvestorUuid(initialInvestorUuid);
    }
  }, [initialInvestorUuid]);

  useEffect(() => {
    if (initialPowerPlantUuids?.length) {
      setSelectedPowerPlantUuids(initialPowerPlantUuids);
    }
  }, [initialPowerPlantUuids]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-gray-800">{report.title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              {requiresSinglePlant ? 'Lütfen raporu oluşturmak için bir santral seçin.' : 'İstersen yatırımcı seçerek raporu daraltabilirsin. Boş bırakırsan tüm kayıtlar gelir.'}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              title="Parametreleri Gör / Debug"
              onClick={() => {
                const p = buildPayload();
                const params = new URLSearchParams();
                if (selectedEmail) params.set('email', selectedEmail);
                if (p.investor?.investor_uuid_id) params.set('investor_uuid', p.investor.investor_uuid_id);
                if (p.investor?.investor_name) params.set('investor_name', p.investor.investor_name);
                if (p.startDate) params.set('start_date', p.startDate);
                if (p.endDate) params.set('end_date', p.endDate);
                if (p.rangeLabel) params.set('range_label', p.rangeLabel);
                if (p.powerPlantUuids?.length) params.set('power_plant_uuids', p.powerPlantUuids.join(','));

                const qs = params.toString();
                const entries = Object.fromEntries(params.entries());

                toast((t) => (
                  <div className="flex flex-col gap-2 min-w-[320px] max-w-[400px]">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                        <Code className="h-4 w-4 text-rose-500" />
                        Gönderilecek Parametreler
                      </div>
                      <button onClick={() => toast.dismiss(t.id)} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3 overflow-x-auto max-h-[300px]">
                      <pre className="text-[11px] font-mono text-emerald-400 whitespace-pre-wrap break-all leading-relaxed">
                        {JSON.stringify(entries, null, 2)}
                      </pre>
                    </div>
                    <div className="text-[9px] text-slate-400 italic break-all">
                      ?{qs}
                    </div>
                  </div>
                ));
              }}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-rose-500 hover:bg-rose-50 transition"
            >
              <Code className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="mt-4">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
            Yatırımcı
          </label>
          <select
            value={selectedInvestorUuid}
            onChange={(e) => setSelectedInvestorUuid(e.target.value)}
            disabled={loading}
            className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300 disabled:cursor-wait disabled:bg-gray-50"
          >
            <option value="">{loading ? 'Yükleniyor...' : (requiresInvestor ? 'Yatırımcı seç' : 'Tümü')}</option>
            {investors.map((investor) => (
              <option key={investor.investor_uuid_id} value={investor.investor_uuid_id}>
                {investor.investor_name}
              </option>
            ))}
          </select>
          {requiresInvestor && !selectedInvestorUuid && !loading && (
            <p className="mt-2 text-xs text-amber-600">Bu rapor için yatırımcı seçimi zorunlu.</p>
          )}
        </div>

        {hasDateRange && (
          <div className="mt-4">
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">
              Tarih Aralığı
            </label>
            <div className="flex flex-wrap gap-2">
              {presetOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setPreset(option.id);
                    if (option.id !== 'custom') {
                      const next = getRangeFromPreset(option.id);
                      setStartDate(next.startDate);
                      setEndDate(next.endDate);
                    }
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                    preset === option.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Başlangıç</span>
                <input
                  type="date"
                  value={startDate}
                  disabled={preset === 'all'}
                  onChange={(e) => {
                    setPreset('custom');
                    setStartDate(e.target.value);
                  }}
                  className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300 disabled:bg-gray-50"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Bitiş</span>
                <input
                  type="date"
                  value={endDate}
                  disabled={preset === 'all'}
                  onChange={(e) => {
                    setPreset('custom');
                    setEndDate(e.target.value);
                  }}
                  className="h-11 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300 disabled:bg-gray-50"
                />
              </label>
            </div>
          </div>
        )}

        {hasPowerPlantSelection && (
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">
                Santraller
              </label>
              {selectedPowerPlantUuids.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedPowerPlantUuids([])}
                  className="text-[11px] font-medium text-slate-500 hover:text-slate-700"
                >
                  Temizle
                </button>
              )}
            </div>
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-gray-200 bg-white p-3">
              {visiblePowerPlants.length === 0 ? (
                <div className="text-xs text-slate-400">Listelenecek santral bulunamadı.</div>
              ) : (
                visiblePowerPlants.map((plant) => {
                  const checked = selectedPowerPlantUuids.includes(plant.power_plant_uuid_id);
                  return (
                    <label key={plant.power_plant_uuid_id} className="flex items-start gap-3 rounded-lg border border-transparent px-1 py-1 text-sm text-gray-700 hover:border-slate-200 hover:bg-slate-50">
                      <input
                        type={requiresSinglePlant ? 'radio' : 'checkbox'}
                        checked={checked}
                        onChange={() => {
                          if (requiresSinglePlant) {
                            setSelectedPowerPlantUuids([plant.power_plant_uuid_id]);
                          } else {
                            setSelectedPowerPlantUuids((prev) =>
                              checked
                                ? prev.filter((item) => item !== plant.power_plant_uuid_id)
                                : [...prev, plant.power_plant_uuid_id],
                            );
                          }
                        }}
                        className={`mt-0.5 h-4 w-4 border-slate-300 text-rose-600 focus:ring-rose-500 ${requiresSinglePlant ? '' : 'rounded'}`}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-slate-800">{plant.power_plant_name}</span>
                        <span className="block truncate text-xs text-slate-500">{plant.power_plant_city} · {plant.power_plant_investor_name}</span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}

        {hasMonthRangeSelection && (
          <div className="mt-4">
            <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-gray-500">
              Ay Aralığı
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Başlangıç Ayı</span>
                <input
                  type="month"
                  value={selectedMonthStart}
                  onChange={(e) => setSelectedMonthStart(e.target.value)}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs text-gray-500">Bitiş Ayı</span>
                <input
                  type="month"
                  value={selectedMonthEnd}
                  onChange={(e) => setSelectedMonthEnd(e.target.value)}
                  className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300"
                />
              </label>
            </div>
          </div>
        )}

        {hasMonthSelection && !hasMonthRangeSelection && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
              Ay
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300"
            />
          </div>
        )}

        {hasSingleDateSelection && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
              Gün
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-rose-300"
            />
          </div>
        )}

        {canSendMail && (
          <div className="mt-4">
            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
              E-posta ile Gönder
            </label>
            <select
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-700 outline-none transition focus:border-amber-300"
            >
              <option value="">E-posta seç</option>
              {users
                .filter((user) => user.is_active === 1 && user.email?.trim())
                .map((user) => (
                  <option key={user.user_uuid_id} value={user.email}>
                    {(user.fullname || user.username) + ' - ' + user.email}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50"
          >
            <Ban className="h-4 w-4" />
            Vazgeç
          </button>
          <button
            type="button"
            onClick={() => onConfirm(buildPayload())}
            disabled={loading || (requiresInvestor && !selectedInvestorUuid) || (requiresSinglePlant && selectedPowerPlantUuids.length === 0)}
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60"
          >
            <Eye className="h-4 w-4" />
            Raporu Aç
          </button>
          {canSendMail && (
            <button
              type="button"
              onClick={async () => {
                setMailSending(true);
                try {
                  await onSend({ ...buildPayload(), email: selectedEmail });
                } finally {
                  setMailSending(false);
                }
              }}
              disabled={loading || mailSending || !selectedEmail || (requiresInvestor && !selectedInvestorUuid) || (requiresSinglePlant && selectedPowerPlantUuids.length === 0)}
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-amber-600 disabled:opacity-60"
            >
              {mailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              Gönder
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PDF Rapor Kartı ──────────────────────────────────────────────────────────

// --- Zamanlama Modalı ---
function ReportScheduleModal({
  report, onClose
}: {
  report: PdfReportDef; onClose: () => void;
}) {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [title, setTitle] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [sql, setSql] = useState("SELECT 'gesmasterinfo@gmail.com' AS email");
  const [cron, setCron] = useState('0 9 * * 1');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const columnDefs = useMemo(() => {
    if (previewData.length === 0) return [];
    return Object.keys(previewData[0]).map(key => ({
      field: key,
      headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      sortable: true,
      filter: true,
      flex: 1,
      minWidth: 120
    }));
  }, [previewData]);

  const loadSchedules = async () => {
    try {
      const data = await fetchReportSchedules(report.id);
      setSchedules(data);
      // İlk schedule'ı otomatik seç (eğer düzenleme modunda değilsek)
      if (data.length > 0) {
        const first = data[0];
        setEditingId(first.id);
        setTitle(first.title || '');
        setIsActive(first.is_active);
        setSql(first.sql_query);
        setCron(first.cron_expression);
        setPreviewData([]);
      }
    } catch (e: any) {
      toast.error('Kayıtlı zamanlamalar getirilemedi: ' + e.message);
    }
  };

  useEffect(() => {
    loadSchedules();
  }, [report.id]);

  const handleSelectSchedule = (sch: ReportSchedule) => {
    setEditingId(sch.id);
    setTitle(sch.title || '');
    setIsActive(sch.is_active);
    setSql(sch.sql_query);
    setCron(sch.cron_expression);
    setPreviewData([]);
  };

  const handleNewSchedule = () => {
    setEditingId(null);
    setTitle('');
    setIsActive(true);
    setSql(`SELECT email, investor_name FROM investors LIMIT 5`);
    setCron('0 9 * * 1');
    setPreviewData([]);
  };

  const handleDeleteSchedule = async (id: number) => {
    if (!(await confirmToast('Bu zamanlama silinsin mi?'))) return;
    setLoading(true);
    try {
      await deleteReportSchedule(id);
      toast.success('Zamanlama silindi');
      if (editingId === id) handleNewSchedule();
      await loadSchedules();
    } catch (e: any) {
      toast.error(e.message || 'Zamanlama silinemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!title.trim() || !sql.trim() || !cron.trim()) {
      toast.error('Başlık, SQL ve Cron alanları zorunludur');
      return;
    }

    setLoading(true);
    try {
      if (editingId) {
        await updateReportSchedule(editingId, { title, sql_query: sql, cron_expression: cron, is_active: isActive });
        toast.success('Zamanlama başarıyla güncellendi!');
      } else {
        const created = await createReportSchedule(report.id, { title, sql_query: sql, cron_expression: cron, is_active: isActive });
        setEditingId(created.id);
        toast.success('Yeni zamanlama oluşturuldu!');
      }
      await loadSchedules();
    } catch (e: any) {
      toast.error(e.message || 'Zamanlama kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!sql.trim()) {
      toast.error('Lütfen bir sorgu girin.');
      return;
    }

    setLoading(true);
    setPreviewData([]);

    try {
      const data = await executeReportSQLPreview(sql);
      setPreviewData(data);
      toast.success('Sorgu başarıyla çalıştırıldı.');
    } catch (error: any) {
      toast.error(error.message || 'Sorgu hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleTestMails = async () => {
    if (previewData.length === 0) {
      toast.error('Önce sorguyu çalıştırıp veri almalısınız.');
      return;
    }

    if (!(await confirmToast('Gerçek mail gönderilsin mi?', `${previewData.length} satır için mail gönderilecek.`))) return;
    void startRealSending();
  };

  const startRealSending = async () => {
    setLoading(true);
    let successCount = 0;

    try {
      for (const row of previewData) {
        const email = row.email || (row.report_payload && row.report_payload.email);
        if (!email) continue;

        const extras: Record<string, string> = {};
        Object.entries(row).forEach(([k, v]) => {
          if (v !== null && v !== undefined && k !== 'report_payload') extras[k] = String(v);
        });
        if (row.report_payload && typeof row.report_payload === 'object') {
          Object.entries(row.report_payload).forEach(([k, v]) => {
            if (v !== null && v !== undefined) extras[k] = String(v);
          });
        }

        await sendReportByEmail(report.id, email, {}, extras);
        successCount++;
      }
      toast.success(`${successCount} rapor mail kuyruğuna alındı!`);
    } catch (error: any) {
      toast.error('Hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl overflow-hidden flex flex-col h-[85vh]">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Clock className="h-5 w-5 text-indigo-600" />
            Otomatik Rapor Zamanla: {report.title}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="h-5 w-5"/></button>
        </div>

        <div className="flex gap-6 flex-1 min-h-0 overflow-y-auto">

          <div className="w-64 flex flex-col min-h-0 border-r border-slate-100 pr-5">
            <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Kayıtlılar</label>
              <button 
                onClick={handleNewSchedule}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2 py-1 rounded"
              >
                + Yeni
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              {schedules.length === 0 && (
                 <div className="text-xs text-slate-400 italic mt-2">Kayıtlı listeleme yok.</div>
              )}
              {schedules.map(sch => {
                const isActiveCard = editingId === sch.id;
                return (
                  <div 
                    key={sch.id} 
                    onClick={() => handleSelectSchedule(sch)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex flex-col gap-1.5 ${isActiveCard ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <div className="flex items-center gap-2 truncate">
                        {!sch.is_active && <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />}
                        <span className={`text-[13px] font-bold truncate ${sch.is_active ? 'text-slate-700' : 'text-slate-400'}`}>{sch.title}</span>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(sch.id); }}
                        className="text-slate-400 hover:text-rose-600 flex-shrink-0"
                        title="Sil"
                      >
                         <Ban className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <span className={`text-[10px] font-mono truncate ${sch.is_active ? 'text-slate-500' : 'text-slate-300'}`}>{sch.cron_expression}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Orta Kolon: SQL, Seçenekler, Önizleme */}
          <div className="flex-1 flex flex-col gap-4 min-h-0">

            {/* BAŞLIK ALANI */}
            <div className="flex-shrink-0">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                Zamanlama Başlığı
                {editingId && <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded text-[9px]">DÜZENLEME: #{editingId}</span>}
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm font-medium focus:border-indigo-500 outline-none transition"
                placeholder="Örn: X Yatırımcısı Günlük SQL..."
              />
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex-shrink-0">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Gönderim Zamanlaması (Cron)</label>
              <div className="flex items-center gap-3">
                <input
                  value={cron}
                  onChange={(e) => setCron(e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-xl font-mono text-sm focus:border-indigo-500 outline-none transition"
                  placeholder="0 9 * * 1 vs."
                />
                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`relative w-11 h-6 rounded-full transition-colors flex items-center shadow-inner flex-shrink-0 ${isActive ? 'bg-indigo-600' : 'bg-slate-200'}`}
                  title={isActive ? 'Aktif' : 'Pasif'}
                >
                  <div className={`absolute w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-200 transform ${isActive ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                  SQL Sorgusu
                </label>
                <button
                  onClick={handlePreview}
                  disabled={loading}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                >
                  {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <Play className="h-3 w-3"/>}
                  Sorguyu Çalıştır
                </button>
              </div>
              <textarea
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                rows={6}
                className="w-full p-4 font-mono text-[14px] bg-slate-900 text-emerald-400 rounded-xl outline-none resize-none leading-relaxed"
                placeholder="SELECT 'gesmasterinfo@gmail.com' AS email, ..."
              />
            </div>

            {/* Sorgu Sonucu */}
            {previewData.length > 0 && (
              <div className="flex flex-col flex-1 min-h-0">
                <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Sorgu Sonucu</label>
                  <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full border border-indigo-100">
                    {previewData.length} Satır Bulundu
                  </span>
                </div>
                <div className="flex-1 border border-slate-200 rounded-xl overflow-hidden bg-white shadow-inner min-h-[160px]">
                  <div className="w-full h-full ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                      modules={enterpriseModules}
                      theme={gridTheme}
                      rowData={previewData}
                      columnDefs={columnDefs}
                      defaultColDef={{
                        resizable: true,
                        sortable: true,
                        filter: true,
                      }}
                      animateRows={true}
                      rowSelection="multiple"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-slate-100 flex justify-end gap-3 flex-shrink-0">
          <button 
            onClick={onClose} 
            className="flex items-center justify-center min-w-[130px] px-4 py-2.5 rounded-xl border border-slate-200 font-bold text-slate-600 hover:bg-slate-50 transition-all text-sm"
          >
            İptal
          </button>

          <button 
            onClick={handleTestMails}
            disabled={loading || previewData.length === 0}
            className="flex items-center justify-center gap-2 min-w-[130px] px-4 py-2.5 rounded-xl bg-amber-500 text-white font-bold hover:bg-amber-600 transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Test
          </button>

          <button 
            onClick={handleSaveSchedule}
            className="flex items-center justify-center gap-2 min-w-[130px] px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all text-sm disabled:opacity-50"
            disabled={loading}
          >
            <Clock className="h-4 w-4" />
            {editingId ? 'Güncelle' : 'Zamanla'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PlantMonthlyCustomReportSettingsModal({
  report,
  onClose,
}: {
  report: PdfReportDef;
  onClose: () => void;
}) {
  const [settings, setSettings] = useState<PlantMonthlyCustomReportSettings>(DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      setLoading(true);
      try {
        const response = await fetchReportSettings<Partial<PlantMonthlyCustomReportSettings>>(report.id);
        if (!mounted) return;
        setSettings({
          ...DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS,
          ...(response.settings_json || {}),
        });
      } catch (error) {
        if (!mounted) return;
        toast.error(error instanceof Error ? error.message : 'Rapor ayarları yüklenemedi.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [report.id]);

  const updateField = <K extends keyof PlantMonthlyCustomReportSettings>(key: K, value: PlantMonthlyCustomReportSettings[K]) => {
    setSettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateReportSettings(report.id, settings);
      toast.success('Rapor ayarları kaydedildi.');
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Rapor ayarları kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const colorFields: Array<{ key: keyof PlantMonthlyCustomReportSettings; label: string }> = [
    { key: 'primaryColor', label: 'Ana Renk' },
    { key: 'accentColor', label: 'Vurgu Rengi' },
    { key: 'tableHeaderBg', label: 'Tablo Başlık Zemini' },
    { key: 'titleColor', label: 'Başlık / Metin Rengi' },
  ];

  const numberFields: Array<{
    key: keyof PlantMonthlyCustomReportSettings;
    label: string;
    min: number;
    max: number;
    step: number;
  }> = [
    { key: 'chartWidthRatio', label: 'Grafik Genişliği Oranı', min: 0.35, max: 0.7, step: 0.01 },
    { key: 'chartHeightMm', label: 'Grafik Yüksekliği (mm)', min: 52, max: 110, step: 1 },
    { key: 'titleFontSize', label: 'Başlık Fontu', min: 10, max: 20, step: 0.5 },
    { key: 'tableFontSize', label: 'Tablo Fontu', min: 5.2, max: 10, step: 0.1 },
    { key: 'detailFontSize', label: 'Detay Fontu', min: 6, max: 11, step: 0.1 },
    { key: 'summaryFontSize', label: 'Özet Fontu', min: 6.5, max: 12, step: 0.1 },
    { key: 'gaugeSizeMm', label: 'Gösterge Boyutu (mm)', min: 24, max: 48, step: 1 },
  ];

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/60 px-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-800">{report.title} Ayarları</h2>
            <p className="mt-1 text-sm text-slate-500">Renk, genişlik ve yerleşim parametrelerini uygulamadan yönetebilirsin.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Ayarlar yükleniyor...
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid gap-2.5 md:grid-cols-2">
              {colorFields.map((field) => (
                <label key={String(field.key)} className="rounded-lg border border-slate-200 p-2.5">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{field.label}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={String(settings[field.key])}
                      onChange={(e) => updateField(field.key, e.target.value as never)}
                      className="h-8 w-10 rounded border border-slate-200 bg-white"
                    />
                    <input
                      type="text"
                      value={String(settings[field.key])}
                      onChange={(e) => updateField(field.key, e.target.value as never)}
                      className="h-8 flex-1 rounded-md border border-slate-200 px-2.5 text-xs text-slate-700 outline-none transition focus:border-rose-300"
                    />
                  </div>
                </label>
              ))}
            </div>

            <div className="grid gap-2.5 md:grid-cols-2">
              {numberFields.map((field) => (
                <label key={String(field.key)} className="rounded-lg border border-slate-200 p-2.5">
                  <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{field.label}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={Number(settings[field.key])}
                      onChange={(e) => updateField(field.key, Number(e.target.value) as never)}
                      className="flex-1 accent-rose-600"
                    />
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={Number(settings[field.key])}
                      onChange={(e) => updateField(field.key, Number(e.target.value) as never)}
                      className="h-8 w-20 rounded-md border border-slate-200 px-2.5 text-xs text-slate-700 outline-none transition focus:border-rose-300"
                    />
                  </div>
                </label>
              ))}
            </div>

            <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <span className="block text-xs font-semibold text-slate-700">Göstergeyi Göster</span>
                <span className="block text-[11px] text-slate-500">Alttaki donut / verimlilik göstergesini raporda tut.</span>
              </div>
              <button
                type="button"
                onClick={() => updateField('showGauge', !settings.showGauge)}
                className={`relative h-7 w-12 rounded-full transition ${settings.showGauge ? 'bg-rose-600' : 'bg-slate-300'}`}
              >
                <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition ${settings.showGauge ? 'left-6' : 'left-1'}`} />
              </button>
            </label>

            <div className="flex justify-between gap-3 border-t border-slate-100 pt-3">
              <button
                type="button"
                onClick={() => setSettings(DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
              >
                Varsayılanlara Dön
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-rose-700 disabled:opacity-60"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Settings2 className="h-4 w-4" />}
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PdfReportCard({
  report,
  onOpen,
  onSettingsOpen,
}: {
  report: PdfReportDef;
  onOpen: (report: PdfReportDef, extra?: { mode?: 'schedule' }) => void;
  onSettingsOpen: (report: PdfReportDef) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      await onOpen(report);
    } catch {
      setError('PDF açılamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const menuItems: DropdownItem[] = [
    {
      label: 'Otomatik Zamanla',
      icon: <Clock className="h-4 w-4" />,
      onClick: () => onOpen(report, { mode: 'schedule' }),
      variant: 'info',
    },
  ];

  if (report.id === 'plant-monthly-custom') {
    menuItems.push({
      label: 'Rapor Ayarları',
      icon: <Settings2 className="h-4 w-4" />,
      onClick: () => onSettingsOpen(report),
      variant: 'warning',
    });
  }

  return (
    <div className="group relative rounded-xl bg-white p-4 text-left shadow-sm transition hover:shadow-md">
      <div className="mb-2.5 flex h-8 w-8 items-center justify-center rounded-lg bg-rose-50 text-rose-600 transition group-hover:bg-rose-100">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
      </div>
      <h2 className="text-sm font-semibold leading-snug text-gray-800">{report.title}</h2>
      <p className="mt-1 text-[11px] leading-5 text-gray-500">{report.description}</p>
      <div className="mt-2.5 flex items-center gap-1.5">
        <span className="inline-block rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-rose-600">
          PDF
        </span>
        <button
          type="button"
          onClick={handleClick}
          disabled={loading}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-[11px] font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-wait disabled:opacity-70"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
          Ac
        </button>
        <DropdownMenu
          align="right"
          title="Rapor İşlemleri"
          items={menuItems}
          trigger={
            <button
              type="button"
              title="Diğer işlemler"
              className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-700 transition hover:bg-slate-100"
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          }
        />
      </div>
    </div>
  );
}

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
export default function ReportsPage() {
  const location = useLocation();
  const [pdfModal, setPdfModal] = useState<{
    report: PdfReportDef;
    url: string;
    options?: ReportOpenOptions;
  } | null>(null);
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [powerPlants, setPowerPlants] = useState<PowerPlant[]>([]);
  const [investorsLoading, setInvestorsLoading] = useState(false);
  const [investorsError, setInvestorsError] = useState<string | null>(null);
  useErrorToast(investorsError);
  const [pendingPdfReport, setPendingPdfReport] = useState<PdfReportDef | null>(null);
  const [pendingInvestorUuid, setPendingInvestorUuid] = useState<string | null>(null);
  const [pendingPowerPlantUuids, setPendingPowerPlantUuids] = useState<string[] | null>(null);
  const [openingReport, setOpeningReport] = useState<{ title: string; description: string } | null>(null);
  const [scheduleModalReport, setScheduleModalReport] = useState<PdfReportDef | null>(null);
  const [settingsModalReport, setSettingsModalReport] = useState<PdfReportDef | null>(null);

  useEffect(() => {
    const state = location.state as { prefillReportId?: string; prefillInvestorUuid?: string; prefillPowerPlantUuids?: string[] } | null;
    const reportId = state?.prefillReportId;
    if (!reportId) return;

    const report = PDF_REPORTS.find((item) => item.id === reportId);
    if (!report) return;

    void (async () => {
      await loadInvestorsForReport(
        report.id === 'plant-daily' ||
          report.id === 'plant-monthly' ||
          report.id === 'plant-monthly-custom' ||
          report.id === 'inverter-daily' ||
          report.id === 'plant-budget-vs-actual',
      );
      setPendingInvestorUuid(state?.prefillInvestorUuid ?? null);
      setPendingPowerPlantUuids(state?.prefillPowerPlantUuids ?? null);
      setPendingPdfReport(report);
      window.history.replaceState({}, document.title);
    })();
  }, [location.state]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      fetchUsers().catch(() => []),
    ])
      .then(([items]) => {
        if (!mounted) return;
        setUsers(items);
      })
      .catch(() => {
        if (!mounted) return;
        setUsers([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const loadInvestorsForReport = async (includePowerPlants = false) => {
    setInvestorsLoading(true);
    setInvestorsError(null);
    try {
      const [rows, plants] = await Promise.all([
        fetchInvestors(),
        includePowerPlants ? fetchPowerPlants() : Promise.resolve([] as PowerPlant[]),
      ]);
      setInvestors(rows.filter((row) => Number(row.plant_count ?? 0) > 0));
      if (includePowerPlants) {
        setPowerPlants(plants);
      }
    } catch {
      setInvestors([]);
      if (includePowerPlants) {
        setPowerPlants([]);
      }
      setInvestorsError('Yatırımcı listesi yüklenemedi.');
    } finally {
      setInvestorsLoading(false);
    }
  };

  const resolveReportExtraParams = async (report: PdfReportDef): Promise<Record<string, string> | undefined> => {
    if (report.id !== 'plant-monthly-custom') return undefined;

    const response = await fetchReportSettings<Partial<PlantMonthlyCustomReportSettings>>(report.id);
    const mergedSettings = {
      ...DEFAULT_PLANT_MONTHLY_CUSTOM_REPORT_SETTINGS,
      ...(response.settings_json || {}),
    };

    return {
      settings_json: JSON.stringify(mergedSettings),
    };
  };

  const handleOpenPdf = async (
    report: PdfReportDef,
    options?: {
      investor?: Investor | null;
      startDate?: string | null;
      endDate?: string | null;
      rangeLabel?: string | null;
      powerPlantUuids?: string[];
    },
  ) => {
    setOpeningReport({
      title: 'Rapor açılıyor',
      description: `${report.title} hazırlanıyor. Lütfen bekleyin.`,
    });
    try {
      const extraParams = await resolveReportExtraParams(report);
      const url = await fetchPdfBlobUrl(report, {
        investorUuid: options?.investor?.investor_uuid_id ?? null,
        investorName: options?.investor?.investor_name ?? null,
        startDate: options?.startDate ?? null,
        endDate: options?.endDate ?? null,
        rangeLabel: options?.rangeLabel ?? null,
        powerPlantUuids: options?.powerPlantUuids ?? null,
        extraParams,
      });
      setPdfModal({
        report,
        url,
        options: {
          investorUuid: options?.investor?.investor_uuid_id ?? null,
          investorName: options?.investor?.investor_name ?? null,
          startDate: options?.startDate ?? null,
          endDate: options?.endDate ?? null,
          rangeLabel: options?.rangeLabel ?? null,
          powerPlantUuids: options?.powerPlantUuids ?? null,
          extraParams,
        },
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Rapor açılamadı.';
      toast.error(message);
      throw error;
    } finally {
      setOpeningReport(null);
    }
  };

  const handleRequestOpenPdf = async (report: PdfReportDef, extra?: { mode?: 'schedule' }) => {
    if (extra?.mode === 'schedule') {
      setScheduleModalReport(report);
      return;
    }
    if (
      report.id === 'powerplants' ||
      report.id === 'problematic-strings-pdf' ||
      report.id === 'investor-daily' ||
      report.id === 'investor-monthly' ||
      report.id === 'plant-daily' ||
      report.id === 'plant-monthly' ||
      report.id === 'plant-monthly-custom' ||
      report.id === 'inverter-daily' ||
      report.id === 'plant-budget-vs-actual'
    ) {
      await loadInvestorsForReport(
        report.id === 'plant-daily' ||
          report.id === 'plant-monthly' ||
          report.id === 'plant-monthly-custom' ||
          report.id === 'inverter-daily' ||
          report.id === 'plant-budget-vs-actual',
      );
      setPendingPdfReport(report);
      return;
    }
    await handleOpenPdf(report);
  };

  const handleCloseModal = () => {
    if (pdfModal) URL.revokeObjectURL(pdfModal.url);
    setPdfModal(null);
  };

  const managementReports = PDF_REPORTS.filter((report) =>
    report.id === 'powerplants' || report.id === 'problematic-strings-pdf' || report.id === 'plant-budget-vs-actual',
  );
  const investorReports = PDF_REPORTS.filter((report) =>
    report.id !== 'powerplants' && report.id !== 'problematic-strings-pdf' && report.id !== 'plant-budget-vs-actual',
  );

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto pb-4">
      {/* ── PDF Raporlar ─────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">Yönetim Raporları</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {managementReports.map((report) => (
            <PdfReportCard key={report.id} report={report} onOpen={handleRequestOpenPdf} onSettingsOpen={setSettingsModalReport} />
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">Yatırımcı Raporları</h2>
          <p className="mt-1 text-xs text-slate-500">Yatırımcı bazında paylaşılabilecek özet ve dönemsel raporlar.</p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {investorReports.map((report) => (
            <PdfReportCard key={report.id} report={report} onOpen={handleRequestOpenPdf} onSettingsOpen={setSettingsModalReport} />
          ))}
        </div>
      </section>

      {/* PDF Modal */}
      {pdfModal && (
        <PdfModal
          title={pdfModal.report.title}
          url={pdfModal.url}
          filename={pdfModal.report.filename}
          users={users}
          report={pdfModal.report}
          options={pdfModal.options}
          onClose={handleCloseModal}
        />
      )}

      {pendingPdfReport && (
        <PowerPlantReportFilterModal
          report={pendingPdfReport}
          investors={investors}
          users={users}
          powerPlants={powerPlants}
          initialInvestorUuid={pendingInvestorUuid}
          initialPowerPlantUuids={pendingPowerPlantUuids}
          loading={investorsLoading}
          onClose={() => {
            setPendingPdfReport(null);
            setPendingInvestorUuid(null);
            setPendingPowerPlantUuids(null);
          }}
          onSend={async ({ investor, email, startDate, endDate, rangeLabel, powerPlantUuids }) => {
            const report = pendingPdfReport;
            if (!report) return;
            try {
              const extraParams = await resolveReportExtraParams(report);
              await sendReportByEmail(report.id, email, {
                investorUuid: investor?.investor_uuid_id ?? null,
                investorName: investor?.investor_name ?? null,
                startDate,
                endDate,
                rangeLabel,
                powerPlantUuids: powerPlantUuids ?? null,
              }, extraParams);
              toast.success('Rapor mail kuyruğuna alındı.');
              setPendingPdfReport(null);
              setPendingInvestorUuid(null);
              setPendingPowerPlantUuids(null);
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Rapor maili gönderilemedi.';
              toast.error(message);
            }
          }}
          onConfirm={async ({ investor, startDate, endDate, rangeLabel, powerPlantUuids }) => {
            const report = pendingPdfReport;
            setPendingPdfReport(null);
            setPendingInvestorUuid(null);
            setPendingPowerPlantUuids(null);
            if (report) {
              try {
                await handleOpenPdf(report, {
                  investor,
                  startDate,
                  endDate,
                  rangeLabel,
                  powerPlantUuids,
                });
              } catch {
                // toast already shown
              }
            }
          }}
        />
      )}

      <BlockingStatusOverlay
        open={Boolean(openingReport)}
        icon={<Sparkles className="h-7 w-7 animate-pulse" />}
        title={openingReport?.title || ''}
        description={openingReport?.description || ''}
      />

      {scheduleModalReport && (
        <ReportScheduleModal 
          report={scheduleModalReport} 
          onClose={() => setScheduleModalReport(null)} 
        />
      )}

      {settingsModalReport && (
        <PlantMonthlyCustomReportSettingsModal
          report={settingsModalReport}
          onClose={() => setSettingsModalReport(null)}
        />
      )}
    </div>
  );
}
